<script setup lang="ts">
import { formatting, Models, useDialogs, useLocalization, useOrderViewContext } from '@hantera/portal-app';
import { ContextMenuSections, FloatingPanel } from '@hantera/design-system';
import { ref, watch } from 'vue';
import AddressEditor from '@/components/AddressEditor';
import useBackend from '@/composition/useBackend';
import AddressDialog from './AddressDialog.vue';

const props = defineProps<{
  address: Models.Graph.Address | Models.Graph.InvoiceRecipient
  deliveries: Models.Graph.Delivery[]
  isInvoiceRecipient: boolean
}>();

const { country } = formatting;
const t = useLocalization();
const orderViewContext = useOrderViewContext();
const editorDialog = ref<typeof FloatingPanel>();
const addressEditor = ref<typeof AddressEditor>();
const backend = useBackend();
const dialogs = useDialogs();

const assignTo = ref<{
  delivery: Models.Graph.Delivery
  assign: boolean
}[]>([]);
const assignToInvoiceRecipient = ref(false);

const editingAddress = ref(props.address);
watch(() => props.address, val => editingAddress.value = val, { deep: true });
watch(() => props.isInvoiceRecipient, () => assignToInvoiceRecipient.value = props.isInvoiceRecipient, { immediate: true });

const dialog = dialogs.add(t('orderView.editAddressTitle'), AddressDialog, true, {
  orderViewContext,
  address: props.address,
  isInvoiceRecipient: props.isInvoiceRecipient,
});

function openEdit() {
  dialog.openModal();
}

const contextMenu = <ContextMenuSections>[[{
  label: 'Copy address',
  action: () => {

    let result = props.address.name

    if (props.address.careOf) {
      result += `\nc/o ${props.address.careOf})`
    }
    result += `\n${props.address.addressLine1}`
    if (props.address.addressLine2) {
      result += `\n${props.address.addressLine2}`
    }

    if (props.address.countryCode === 'US') {
      result += `\n${props.address.city}, ${props.address.state} ${props.address.postalCode}`
    } else {
      result += `\n${props.address.postalCode} ${props.address.city}`

      if (props.address.state) {
        result += `, ${props.address.state}`
      }
    }

    result += `\n${country(props.address.countryCode)}`

    if (props.address.email || props.address.phone) {
      result += `\n`
    }
    if (props.address.email) {
      result += `\nE-mail: ${props.address.email}`
    }
    if (props.address.phone) {
      result += `\nPhone: ${props.address.phone}`
    }

    navigator.clipboard.writeText(result);
  }
}]]

</script>

<template>
  <div class="order-address">
    <div class="order-address_address" v-context-menu="contextMenu">
      <div>{{ address.name }}</div>
      <div>{{ address.addressLine1 }}</div>
      <div>{{ address.addressLine2 }}</div>
      <div>{{ address.postalCode }} {{ address.city }}<span v-if="address.state">, {{
        address.state }}</span>
      </div>
      <div>{{ country(address.countryCode) }}</div>
      <div>{{ address.email }}</div>
      <div>{{ address.phone }}</div>
      <div v-if="isInvoiceRecipient && (address as Models.Graph.InvoiceRecipient).taxId">{{ (address as Models.Graph.InvoiceRecipient).taxId }}</div>
      <div class="order-address__tags">
        <div
          v-if="isInvoiceRecipient"
          class="order-address__tag order-address__invoice-address-tag"
        >{{ t('orderView.invoiceRecipient') }}</div>
        <div
          v-for="delivery in deliveries"
          class="order-address__tag"
          :style="`border-color: var(--state-colors__delivery-${delivery.deliveryState.toLowerCase()})`"
        >{{ delivery.deliveryNumber }}</div>
      </div>
    </div>

    <div class="order-address__edit-container">
      <button
        @click="openEdit"
      >{{ t('common.edit') }}</button>
    </div>
  </div>
</template>

<style scoped>
.order-address {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto min-content;
  gap: 10px;
  border-bottom: 1px solid hsl(var(--color-70));
  padding: 5px;
}

.order-address:last-child {
  border-bottom: none;
}

.order-address__edit-container {
  display: flex;
  align-items: center;
}

.order-address__tags {
  display: flex;
  gap: 5px;
  font-size: 12px;
  flex-wrap: wrap;
}

.order-address__tag {
  padding: 3px 3px 1px 3px;
  margin-top: 2px;
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
}

.order-address__invoice-address-tag {
  background-color: hsl(var(--color-70));
  border-radius: 4px;
  border-color: hsl(var(--color-70));
}
</style>